import React, { Suspense, useEffect } from "react";
import {
  Button,
  Divider,
  Flex,
  Heading,
  SelectField,
  TextField,
  PhoneNumberField,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useProfileQuery } from "./api/useProfileQuery";
import { resolver } from "./validations/aboutMeValidations";
import {
  PostProfileMutation,
  PutProfileMutation,
} from "./api/useProfileMutation";
import { useAuth } from "../../ui-components/utils";
import { useUserQuery } from "../common/useUserQuery";
import { useNavigate } from "react-router-dom";
import { useProfileGeneralQuery } from "./api/useProfileGeneralQuery";
import {
  PostGeneralMutation,
  PutGeneralMutation,
} from "./api/useProfileGeneralMutation";
import { useProfileContactQuery } from "./api/useProfileContactQuery";
import { useEmergencyContactsQuery } from "./api/useEmergencyContactsQuery";
import {
  useProfileContactPostMutation,
  useProfileContactPutMutation,
} from "./api/useProfileContactMutation";
import {
  useEmergencyContactDeleteMutation,
  useEmergencyContactPostMutation,
  useEmergencyContactPutMutation,
} from "./api/useEmergencyContactMutation";
import EmergencyContactFormContainer from "./containers/EmergencyContactFormContainer";
import ProfileContactFormContainer from "./containers/ProfileContactFormContainer";

export default function AboutMePage() {
  const navigate = useNavigate();
  const authAttributes = useAuth().user?.attributes ?? {};

  /* QUERIES */
  const { data: userQueryData } = useUserQuery();
  const {
    data: profileResult,
    isLoading,
    isSuccess,
  } = useProfileQuery(userQueryData?.patientUserId);
  const { data: generalResult } = useProfileGeneralQuery(
    userQueryData?.patientUserId
  );
  const { data: profileContact } = useProfileContactQuery(
    userQueryData?.patientUserId
  );
  const { data: emergencyContacts } = useEmergencyContactsQuery(
    userQueryData?.patientUserId
  );

  /*  MUTATIONS */
  const {
    mutate: postProfileMutation,
    isLoading: isLoadingPost,
    isSuccess: isSuccessPost,
  } = PostProfileMutation();
  const {
    mutate: putProfileMutation,
    isLoading: isLoadingPut,
    isSuccess: isSuccessPut,
  } = PutProfileMutation();

  const { mutate: putGeneral } = PutGeneralMutation();
  const { mutate: postGeneral } = PostGeneralMutation();

  const { mutate: profileContactPost } = useProfileContactPostMutation(
    userQueryData?.patientUserId
  );
  const { mutate: profileContactPut } = useProfileContactPutMutation();

  const { mutate: emergencyContactAdd } = useEmergencyContactPostMutation(
    userQueryData?.patientUserId
  );
  const { mutate: emergencyContactUpdate } = useEmergencyContactPutMutation();
  const { mutate: emergencyContactDelete } =
    useEmergencyContactDeleteMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: resolver,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "emergencyContacts",
    keyName: "key",
  });

  useEffect(() => {
    if (
      profileResult &&
      profileContact &&
      emergencyContacts &&
      authAttributes["sub"]
    ) {
      console.log("useEffect", { profileResult, authAttributes });
      reset({
        givenName: profileResult?.givenName || authAttributes["given_name"],
        nickname: profileResult?.nickname || "",
        email: profileResult?.email || authAttributes["email"],
        dob: profileResult?.dob || authAttributes["birthdate"],
        primaryPhone:
          profileResult?.primaryPhone || authAttributes["phone_number"],
        gender: profileResult?.gender || "",
        ssn: profileResult?.ssn || "",
        maritalStatus: profileResult?.maritalStatus || "",
        genderIdentity: profileResult?.genderIdentity || "",
        sexualOrientation: profileResult?.sexualOrientation || "",
        otherFirstName: profileResult?.otherFirstName || "",
        otherLastName: profileResult?.otherLastName || "",
        otherMiddleName: profileResult?.otherMiddleName || "",
        profileContact: profileContact || {},
        emergencyContacts: emergencyContacts ?? [],
      });
    }
  }, [profileResult, authAttributes["sub"], profileContact, emergencyContacts]);

  const onEmergencyContactDelete = (ecId) => {
    emergencyContactDelete(ecId);
  };

  const onSubmit = async (values) => {
    const pPhone = !values?.profileContact.primaryPhone
      ? null
      : `${values.profileContact.primaryDialCode}-${values.profileContact.primaryPhone}`;
    const profileParams = {
      patientUserId: userQueryData?.patientUserId,
      ...values,
      primaryPhone: pPhone,
    };

    if (profileResult && profileResult.patientUserId) {
      const dirtyPayload = JSON.parse(JSON.stringify(profileParams));
      putProfileMutation({ ...dirtyPayload, ssn: profileParams?.ssn || null });
    } else {
      postProfileMutation({
        ...profileParams,
        ssn: profileParams?.ssn || null,
      });
    }
    if (generalResult && generalResult.patientUserId) {
      putGeneral({
        patientUserId: userQueryData?.patientUserId,
        ...values.generalResult,
      });
    } else {
      postGeneral({
        patientUserId: userQueryData?.patientUserId,
        ...values.generalResult,
      });
    }

    const sPhone = !values.profileContact.secondaryPhone
      ? null
      : `${values.profileContact.secondaryDialCode}-${values.profileContact.secondaryPhone}`;
    const wPhone = !values.profileContact.workPhone
      ? null
      : `${values.profileContact.workDialCode}-${values.profileContact.workPhone}`;

    const contactParams = {
      id: userQueryData?.patientUserId,
      address1: values?.profileContact.address1,
      address2: values?.profileContact.address2,
      city: values?.profileContact.city,
      state: values?.profileContact.state,
      zip: values?.profileContact.zip,
      primaryPhone: pPhone,
      secondaryPhone: sPhone,
      workPhone: wPhone,
    };

    if (profileContact && profileContact.id) {
      const dirtyPayload = JSON.parse(JSON.stringify(contactParams));
      profileContactPut(dirtyPayload);
    } else {
      await profileContactPost(contactParams);
    }

    for (let item of values.emergencyContacts) {
      const phoneFormat = !item.phone ? null : `${item.dialCode}-${item.phone}`;
      item = {
        ...item,
        phone: phoneFormat,
      };
      if (!item.id) {
        await emergencyContactAdd({
          profileId: userQueryData?.patientUserId,
          ...item,
        });
      } else {
        emergencyContactUpdate(item);
      }
    }
    navigate("/user/quick-actions/profile-summary");
  };

  if (isLoading) return "Loading...";

  if (isLoadingPost || isLoadingPut) return "Saving changes...";

  if (isSuccess)
    return (
      <>
        {isSuccessPost || (isSuccessPut && <>Changes saved</>)}
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"About Me"}>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="givenName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth={300}
                    label="*Given name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.givenName?.message}
                    hasError={!!errors.givenName}
                  />
                )}
              />
              <Controller
                name="dob"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth={300}
                    label="*Date of birth"
                    {...field}
                    placeholder="YYYY-MM-DD"
                    value={field.value || ""}
                    errorMessage={errors.dob?.message}
                    hasError={!!errors.dob}
                  />
                )}
              />
            </Flex>
            <Flex
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="nickname"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth={300}
                    label="Nickname/Preferred Name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.nickname?.message}
                    hasError={!!errors.nickname}
                  />
                )}
              />
            </Flex>
            <Flex
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              minWidth={300}
            >
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth={300}
                    //disabled={true}
                    label="*Email"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.email?.message}
                    hasError={!!errors.email}
                  />
                )}
              />
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
              minWidth={300}
            >
              <Controller
                name="ssn"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth={300}
                    label="Social security"
                    placeholder="000-00-0000"
                    {...field}
                    value={field.value || ""}
                    maxLength={11}
                    errorMessage={errors.ssn?.message}
                    hasError={!!errors.ssn}
                  />
                )}
              />
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Sex"
                    placeholder="Select"
                    {...field}
                    value={field.value || ""}
                  >
                    <option value="FEMALE">Female</option>
                    <option value="MALE">Male</option>
                    <option value="NOT_SPECEFIED">Not Specefied</option>
                  </SelectField>
                )}
              />
            </Flex>
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
              minWidth={300}
            >
              <Controller
                name="maritalStatus"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Marital status"
                    placeholder="Select"
                    {...field}
                    value={field.value || ""}
                  >
                    <option value="SINGLE">Single</option>
                    <option value="MARRIED">Married</option>
                  </SelectField>
                )}
              />
              <Controller
                name="sexualOrientation"
                control={control}
                render={({ field }) => (
                  <SelectField
                    minWidth="300px"
                    label="Sexual orientation"
                    placeholder="Select"
                    {...field}
                    value={field.value || ""}
                  >
                    <option value="HETEROSEXUAL">Heterosexual</option>
                    <option value="LESBIAN">Lesbian</option>
                    <option value="GAY">Gay</option>
                    <option value="BI_SEXUAL">Bi-Sexual</option>
                    <option value="PAN_SEXUAL">Pan-Sexual</option>
                    <option value="A_SEXUAL">A-Sexual</option>
                    <option value="QUEER">Queer</option>
                    <option value="DECLINE_TO_STATE">Decline to State</option>
                  </SelectField>
                )}
              />
            </Flex>
            <Controller
              name="genderIdentity"
              control={control}
              render={({ field }) => (
                <SelectField
                  minWidth="300px"
                  label="Gender identity"
                  placeholder="Select"
                  {...field}
                  value={field.value || ""}
                >
                  <option value="MAN">Man</option>
                  <option value="WOMAN">Woman</option>
                  <option value="TRANSGENDER_MAN">Transgender Man</option>
                  <option value="TRANSGENDER_WOMAN">Transgender Woman</option>
                  <option value="NON_BINARY">Non-Binary</option>
                  <option value="AGENDER">
                    Agender/I don’t identify with any gender
                  </option>
                  <option value="DECLINE_TO_STATE">Decline to State</option>
                </SelectField>
              )}
            />
            <Divider
              width="unset"
              height="1px"
              shrink="0"
              alignSelf="stretch"
              size="small"
              orientation="horizontal"
            />
            <Flex
              direction={{ base: "column", medium: "row" }}
              justifyContent="flex-start"
              alignItems="flex-start"
            >
              <Controller
                name="generalResult.language"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Language"
                    {...field}
                    value={field.value ?? generalResult?.language}
                    errorMessage={errors.language?.message}
                    hasError={!!errors.language}
                  />
                )}
              />
              <Controller
                name="generalResult.race"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Race"
                    {...field}
                    value={field.value ?? generalResult?.race}
                    errorMessage={errors.race?.message}
                    hasError={!!errors.race}
                  />
                )}
              />
            </Flex>
            <Controller
              name="generalResult.religion"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="300px"
                  label="Religion"
                  {...field}
                  value={field.value ?? generalResult?.religion}
                />
              )}
            />
            <Divider
              width="unset"
              height="1px"
              shrink="0"
              alignSelf="stretch"
              size="small"
              orientation="horizontal"
            />
            <Heading level="6" children="Other Name"></Heading>
            <Flex gap="24px" direction={{ base: "column", medium: "row" }}>
              <Controller
                name="otherFirstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="First name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.otherFirstName?.message}
                    hasError={!!errors.otherFirstName}
                  />
                )}
              />
              <Controller
                name="otherLastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    minWidth="300px"
                    label="Last name"
                    {...field}
                    value={field.value || ""}
                    errorMessage={errors.otherLastName?.message}
                    hasError={!!errors.otherLastName}
                  />
                )}
              />
            </Flex>
            <Controller
              name="otherMiddleName"
              control={control}
              render={({ field }) => (
                <TextField
                  minWidth="160px"
                  label="Middle name"
                  {...field}
                  value={field.value || ""}
                  errorMessage={errors.otherMiddleName?.message}
                  hasError={!!errors.otherMiddleName}
                />
              )}
            />
            <Divider size="small" />
            <Heading level="6" children="Contact Info"></Heading>
            <ProfileContactFormContainer
              control={control}
              profileContact={profileContact}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
            />
            <Suspense fallback="Loading...">
              <EmergencyContactFormContainer
                control={control}
                append={append}
                remove={remove}
                fields={fields}
                onEmergencyContactDelete={onEmergencyContactDelete}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
              />
            </Suspense>
            <Flex gap="18px" direction="column">
              <Button variation="primary" type="submit">
                Save
              </Button>
            </Flex>
          </FlexFormContainer>
        </form>
      </>
    );
}
